import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";

interface DocLinkState {
  isLoading: boolean;
  error: string | null;
  createDocLink: (
    docDetails: DocLinkDetails,
    onSuccess?: (doc: DocLinkDetails) => void
  ) => Promise<DocLinkDetails>;
  getDocLink: (
    parentId: string,
    path?: string,
    onSuccess?: (doc: DocLinkDetails[]) => void
  ) => Promise<DocLinkDetails[]>;
  updateDocLink: (
    docDetails: DocLinkDetails,
    onSuccess?: (doc: DocLinkDetails) => void
  ) => Promise<DocLinkDetails>;
  deleteDocLink: (
    parentId: string,
    path?: string,
    onSuccess?: (docs: DocLinkDetails[]) => void
  ) => Promise<DocLinkDetails[]>;
}

const useDocLinks = (): DocLinkState => {
  const [error, setError] = useState<string | null>(null);
  const {
    postData: createDocLinkRequest,
    loading: createDocLinkRequestLoading,
  } = useApi<DocLinkDetails>();

  const {
    putData: updateDocLinkRequest,
    loading: updateDocLinkRequestLoading,
  } = useApi<DocLinkDetails>();

  const { getData: getDocLinkRequest, loading: getDocLinkRequestLoading } =
    useApi<DocLinkDetails[]>();

  const {
    deleteData: deleteDocLinkRequest,
    loading: deleteDocLinkRequestLoading,
  } = useApi<DocLinkDetails[]>();

  const createDocLink = async (
    docDetails: DocLinkDetails,
    onSuccess?: (doc: DocLinkDetails) => void
  ) => {
    try {
      setError(null);
      const response = await createDocLinkRequest(
        API_ROUTES.DOC_LINK.base,
        docDetails,
        onSuccess
      );

      return Promise.resolve(response);
    } catch (error) {
      setError(error.message);
      return Promise.reject(error.message);
    }
  };

  const getDocLink = async (
    parentId: string,
    path?: string,
    onSuccess?: (doc: DocLinkDetails[]) => void
  ) => {
    try {
      setError(null);
      const response = await getDocLinkRequest(
        `${API_ROUTES.DOC_LINK.base}/${parentId}?path=${path}`,
        onSuccess
      );

      return Promise.resolve(response);
    } catch (error) {
      setError(error.message);
      return Promise.reject(error.message);
    }
  };

  const updateDocLink = async (
    docDetails: DocLinkDetails,
    onSuccess?: (doc: DocLinkDetails) => void
  ) => {
    try {
      setError(null);
      const response = await updateDocLinkRequest(
        API_ROUTES.DOC_LINK.base,
        docDetails,
        onSuccess
      );

      return Promise.resolve(response);
    } catch (error) {
      setError(error.message);
      return Promise.reject(error.message);
    }
  };

  const deleteDocLink = async (
    parentId: string,
    path?: string,
    onSuccess?: (docs: DocLinkDetails[]) => void
  ) => {
    try {
      setError(null);
      const response = await deleteDocLinkRequest(
        `${API_ROUTES.DOC_LINK.base}/${parentId}`,
        path ? { path } : null,
        onSuccess
      );

      return Promise.resolve(response);
    } catch (error) {
      setError(error.message);
      return Promise.reject(error.message);
    }
  };

  return {
    error,
    isLoading:
      createDocLinkRequestLoading ||
      updateDocLinkRequestLoading ||
      getDocLinkRequestLoading ||
      deleteDocLinkRequestLoading,
    createDocLink,
    getDocLink,
    updateDocLink,
    deleteDocLink,
  };
};

export default useDocLinks;
